import { useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { useChatStore } from '@/stores/chat-store'
import { useIsDesktop } from '@/hooks/use-is-desktop'
import { ChatDrawerShell } from './chat-drawer'
import { CompanionFloatingWindow } from './companion-floating-window'
import { useCompanionConversation } from './use-companion-conversation'
import { useVoiceSession } from './voice/use-voice-session'
import { useAiChatShortcut } from './use-ai-chat-shortcut'
import { installLastPanelTracker } from '@/features/companion/last-panel-tracker'

/**
 * Single mount point for the companion conversation and its surfaces.
 *
 * Owns the ONE useCompanionConversation call (mounting the hook twice would
 * double the manager subscription and race the IDB rehydrate) and picks the
 * shell per the store's surface preference:
 *  - floating: the draggable overlay window (desktop only)
 *  - drawer:   the classic right-edge panel
 *
 * The drawer shell stays mounted either way — its closed state is width 0,
 * and keeping it in the flex row preserves the layout contract with
 * app-layout (mounted OUTSIDE SheetContainerProvider so sheets never cover
 * the companion; the floating window is fixed-position, same rule).
 */
export function CompanionHost() {
  const conversation = useCompanionConversation()
  // Single mount for the composer-voice side effects (same rule as the
  // conversation hook — see voice/use-voice-session.ts).
  useVoiceSession()
  // Mod+K opens, focuses and closes the chat from anywhere in the app.
  useAiChatShortcut()
  // Which chart the user last used, for the chat's "Open on chart".
  useEffect(() => installLastPanelTracker(), [])
  const open = useChatStore((s) => s.open)
  const surface = useChatStore((s) => s.surface)
  const isDesktop = useIsDesktop()
  const effectiveSurface = isDesktop ? surface : 'drawer'

  return (
    <>
      <ChatDrawerShell conversation={conversation} active={effectiveSurface === 'drawer'} />
      <AnimatePresence>
        {open && effectiveSurface === 'floating' && (
          <CompanionFloatingWindow conversation={conversation} />
        )}
      </AnimatePresence>
    </>
  )
}
